<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { Play, Pause, Music, FileImage, Image, Video, AudioLines, Trash2 } from 'lucide-vue-next'
import MediaSliderDialog from './MediaSliderDialog.vue'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import { escapeHtml } from '@/utils/strings'

const props = defineProps({
  resources: {
    type: Array,
    default: () => []
  },
  showDelete: {
    type: Boolean,
    default: true
  },
  onDelete: {
    type: Function,
    default: null
  }
})

const emit = defineEmits(['delete'])

const sliderOpen = ref(false)
const selectedIndex = ref(0)
const deleteDialog = ref(null)
const pendingDeleteResource = ref(null)


// Process resources - group by UUID and handle both old and new format
const processedResources = computed(() => {
  if (!props.resources || props.resources.length === 0) return []

  // Group resources by UUID
  const grouped = {}

  props.resources.forEach(resource => {
    const uuid = resource.uuid

    if (!grouped[uuid]) {
      grouped[uuid] = {
        uuid: uuid,
        file_type: resource.file_type || 'image', // default to image for legacy
        filename: resource.filename,
        variants: {}
      }
    }

    // Store variant based on type
    if (resource.type === 'small' || resource.type === 'thumbnail' || resource.type === 'fullsize') {
      grouped[uuid].variants[resource.type] = resource
      grouped[uuid].file_type = resource.file_type || 'image'
    } else if (resource.type === 'original') {
      // Video/audio with type="original"
      grouped[uuid].variants['original'] = resource
      grouped[uuid].file_type = resource.file_type
    } else {
      // Legacy format compatibility
      grouped[uuid].variants[resource.type || 'fullsize'] = resource
    }

    // Update filename if available
    if (resource.filename) {
      grouped[uuid].filename = resource.filename
    }
  })

  // Convert to array and select best variant for display
  return Object.values(grouped).map(group => {
    // Select the best variant for preview
    const variant = group.variants.thumbnail || group.variants.small || group.variants.fullsize || group.variants.original || Object.values(group.variants)[0]

    return {
      ...variant,
      uuid: group.uuid,
      type: group.file_type, // Use file_type for component logic
      file_type: group.file_type,
      filename: group.filename || variant.filename,
      variant_urls: {
        small: group.variants.small?.url,
        thumbnail: group.variants.thumbnail?.url,
        fullsize: group.variants.fullsize?.url,
        original: group.variants.original?.url
      },
      all_variants: group.variants // Keep all variants for deletion
    }
  })
})

const hasMedia = computed(() => {
  return processedResources.value.length > 0
})

const handlePreviewClick = (index, resource) => {
  selectedIndex.value = index
  sliderOpen.value = true
}


const setPendingDelete = async (resource) => {
  pendingDeleteResource.value = resource
  await nextTick()
  if (deleteDialog.value) {
    deleteDialog.value.open()
  }
}

const confirmDelete = () => {
  if (pendingDeleteResource.value) {
    const resourceToDelete = pendingDeleteResource.value

    if (props.onDelete) {
      props.onDelete(resourceToDelete)
    }

    emit('delete', resourceToDelete)

    if (processedResources.value.length === 1) {
      sliderOpen.value = false
    }
    pendingDeleteResource.value = null
  }
}

const handleResourceDelete = (resource) => {
  emit('delete', resource)
  // Close the slider if deleting the last resource
  if (processedResources.value.length === 1) {
    sliderOpen.value = false
  }
}

// Computed property for delete confirmation message
const deleteConfirmMessage = computed(() => {
  const filename = pendingDeleteResource.value?.filename || 'this resource'
  const escaped = escapeHtml(filename)
  return `Are you sure you want to delete '${escaped}'? This action cannot be undone.`
})

</script>

<template>
  <div v-if="hasMedia" class="media-list">
    <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
      <div
        v-for="(resource, index) in processedResources"
        :key="resource.id || resource.uuid || index"
        class="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer hover:opacity-90 transition-opacity"
      >
        <!-- Click area for preview -->
        <div
          @click="handlePreviewClick(index, resource)"
          class="absolute inset-0 z-10"
        >
          <template v-if="resource.type === 'image'">
            <img
              :src="resource.variant_urls?.thumbnail || resource.thumbnail_url || resource.url"
              :alt="resource.filename || 'Image'"
              class="w-full h-full object-cover"
              loading="lazy"
            />
          </template>

          <!-- Video preview -->
          <template v-else-if="resource.type === 'video'">
            <!-- Use thumbnail or placeholder -->
            <div class="w-full h-full bg-gray-200 flex items-center justify-center">
              <Video class="w-8 h-8 text-gray-500" />
            </div>
            <!-- Video indicator overlay -->
            <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div class="bg-black/50 rounded-full p-2">
                <Play class="w-3 h-3 text-white fill-white" />
              </div>
            </div>
            <!-- Video type indicator -->
            <div class="absolute bottom-1 left-1 bg-black/50 rounded-md p-0.5 pointer-events-none">
              <Video class="w-3.5 h-3.5 text-white" />
            </div>
          </template>

          <!-- Audio preview -->
          <template v-else-if="resource.type === 'audio'">
            <div class="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600">
              <!-- Play button -->
              <div class="bg-white/20 rounded-full p-2">
                <Play class="w-3 h-3 text-white fill-white" />
              </div>

              <!-- Audio type indicator -->
              <div class="absolute bottom-1 left-1 bg-white/20 rounded-md p-0.5">
                <AudioLines class="w-3.5 h-3.5 text-white" />
              </div>
            </div>
          </template>

          <!-- Other file types -->
          <template v-else>
            <div class="w-full h-full flex items-center justify-center bg-gray-200">
              <FileImage class="w-12 h-12 text-gray-500" />
            </div>
          </template>
        </div>

        <!-- Delete button overlay (shows on hover) -->
        <div
          v-if="showDelete"
          class="absolute top-1 right-1 z-20 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <button
            @click.stop="setPendingDelete(resource)"
            class="p-1.5 bg-red-600/90 hover:bg-red-600 rounded-full transition-colors shadow-lg"
          >
            <Trash2 class="w-3.5 h-3.5 text-white" />
          </button>
        </div>
      </div>
    </div>

    <!-- Delete confirmation dialog -->
    <DialogConfirm
      ref="deleteDialog"
      :title="'Delete Resource'"
      :description="deleteConfirmMessage"
      @confirm="confirmDelete"
    />

    <!-- Media slider dialog -->
    <MediaSliderDialog
      v-model:open="sliderOpen"
      :resources="processedResources"
      :initial-index="selectedIndex"
      :show-delete="showDelete"
      @delete="handleResourceDelete"
    />
  </div>
</template>
